import React, { useEffect, useState } from 'react';
import { Box, Image, Text } from '@chakra-ui/react';
import { AxiosError } from 'axios';
import { getBaseUrl } from 'api';
import { Location, WidgetData } from '../types';
import {
  MainPageHeader,
  Page,
  Plate,
  Loader,
  NotFoundAlert,
  QuickSearch,
} from '../components';

type DivisionType = {
  id: string;
  name: string;
  address: string;
  pictureId: string;
  description: string;
};

export const DivisionPage: React.FC<{
  data: WidgetData;
  loading?: boolean;
  error?: AxiosError<unknown> | boolean;
  locality: string;
  divisions: DivisionType[];
  backToLocality: boolean;
  setLocality: (locality: Location | null) => void;
  setDivision: (division: string | null) => void;
}> = ({
  data,
  loading,
  error,
  locality,
  divisions,
  backToLocality,
  setLocality,
  setDivision,
}) => {
  const [filteredDivisions, setFilteredDivisions] =
    useState<DivisionType[]>(divisions);
  const [searchValue, setSearchValue] = useState<string>('');

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setSearchValue(event.target.value);

  const filterValues = (
    searchText: string,
    list: DivisionType[],
  ): DivisionType[] => {
    if (!searchText) return list;

    return list.filter(
      (item) =>
        item.name.toLowerCase().includes(searchText.toLowerCase()) ||
        item.address.toLowerCase().includes(searchText.toLowerCase()),
    );
  };

  useEffect(() => {
    const Debounce = setTimeout(() => {
      const values = filterValues(searchValue, divisions);
      setFilteredDivisions(values);
    }, 300);

    return () => clearTimeout(Debounce);
  }, [searchValue, divisions]);

  return (
    <Page
      // eslint-disable-next-line prettier/prettier
      title={(
        <MainPageHeader
          name={data.company.name}
          type={data.company.type}
          address={locality}
        />
        // eslint-disable-next-line prettier/prettier
      )}
      onBackClick={backToLocality ? () => setLocality(null) : undefined}
    >
      <Loader loading={loading} error={error} />
      <Box>
        {data.company.pictureId && (
          <Image
            w="100%"
            maxH="306px"
            objectFit="cover"
            borderRadius={{
              base: '8px',
              md: '20px',
            }}
            src={`${getBaseUrl()}/booking-widget/client/attachment/file/${
              data.company.pictureId
            }/inline`}
            alt="Фото"
            loading="eager"
            mb="4"
          />
        )}
        <QuickSearch
          value={searchValue}
          onChange={handleFilterChange}
          placeholder="Поиск филиала"
        />
        <Box w="100%" borderRadius="lg">
          {filteredDivisions.length ? (
            filteredDivisions.map((division) => (
              <Plate key={division.id} onClick={() => setDivision(division.id)}>
                <Box ml={4}>
                  <Text color="black" textStyle="h4" mb={1} translate="no">
                    {division.name}
                  </Text>
                  <Text color="grayText" textStyle="body2" translate="no">
                    {division.address}
                  </Text>
                </Box>
              </Plate>
            ))
          ) : (
            <NotFoundAlert text="Филиал не найден" />
          )}
        </Box>
      </Box>
    </Page>
  );
};
